import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import React, { useCallback, useEffect } from "react";
import { WSQueryCache } from "@ws-react-query";
import { RouteComponentProps } from "react-router-dom";
import { sessionService } from "@services/sessionService";
import { clearAllDynamicFormDrafts } from "../modules/DynamicForm/utils/deriveDraftKey";
import { deleteAllSessionTokens } from "../services/sessionStorage";
import { useResetWSStore } from "../store";
import { analyticsService } from "@services/analyticsService";

export const SignOut: React.FC<
  RouteComponentProps<
    {},
    {},
    {
      forceLogout?: boolean;
    }
  >
> = ({ history, location }) => {
  const resetWSStore = useResetWSStore();
  const snackbar = useWSSnackbar();

  const reset = useCallback(() => {
    WSQueryCache.clear();
    window.sessionStorage.removeItem("partner");
    window.localStorage.removeItem("signUp1099");
    window.localStorage.removeItem("invoiceCurrency");
    clearAllDynamicFormDrafts();
    // Sign-in only ever creates a v3 session, but the remaining v1-token flows
    // (password reset, the embedded app, impersonation) can still leave v1
    // tokens in storage — drop them so nothing outlives the sign-out.
    deleteAllSessionTokens();
    resetWSStore();

    analyticsService.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    if (location.state?.forceLogout) {
      snackbar.openSnackbar({
        message:
          "For security reasons, your login has timed out. Please log in again to continue.",
        icon: {
          name: "logout"
        }
      });
    }

    // Deletes the session server-side and clears the in-memory access token
    // and the persisted refresh token, leaving no tokens behind. It clears the
    // local tokens even when the server-side delete fails, so a failure here
    // must not skip the local cleanup or the redirect below.
    try {
      await sessionService.logout();
    } catch (error) {
      console.error(error);
    }

    reset();

    analyticsService.track("Auth", { action: "Log Out" });

    history.replace(location.hash ? location.hash.slice(1) : "/member/sign-in");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
